import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import api from 'lib/api';

function CircularProgressRing({ percentage, size = 80, strokeWidth = 7 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage === 100 ? 'var(--color-success)' : percentage >= 50 ? 'var(--color-primary)' : 'var(--color-warning)';
  return (
    <svg width={size} height={size} className="-rotate-90" style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--color-muted)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
    </svg>
  );
}

export default function ProfileCompletionCard({ userId, onDismiss }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const fetchCompletionData = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: p } = await api.get('/profiles/me');

      const avatarBasicComplete = !!(
        p?.avatar_url &&
        p?.full_name?.trim() &&
        p?.phone?.trim() &&
        p?.date_of_birth &&
        p?.gender
      );

      const emergencyComplete = !!(
        p?.emergency_contact_name?.trim() &&
        p?.emergency_contact_phone?.trim()
      );

      const workoutComplete = !!(
        p?.preferences?.workout?.workoutTypes?.length > 0
      );

      const goalsDietComplete = !!(
        p?.preferences?.goal?.primaryGoal &&
        p?.preferences?.diet?.dietaryPreference
      );

      setSections([
        {
          id: 'avatar',
          label: 'Avatar & Basic Info',
          description: 'Photo, name, phone, DOB, gender',
          complete: avatarBasicComplete,
          icon: 'User',
          link: '/profile-update-page',
          linkLabel: 'Update Profile',
        },
        {
          id: 'emergency',
          label: 'Emergency Contact',
          description: 'Emergency contact name & phone',
          complete: emergencyComplete,
          icon: 'Phone',
          link: '/profile-update-page',
          linkLabel: 'Add Contact',
        },
        {
          id: 'preferences',
          label: 'Workout Preferences',
          description: 'Workout types, schedule, experience',
          complete: workoutComplete,
          icon: 'Dumbbell',
          link: '/guided-onboarding-flow',
          linkLabel: 'Set Preferences',
        },
        {
          id: 'goals',
          label: 'Goals & Diet',
          description: 'Fitness goals and dietary intake',
          complete: goalsDietComplete,
          icon: 'Target',
          link: '/guided-onboarding-flow',
          linkLabel: 'Set Goals',
        },
      ]);
    } catch (err) {
      console.error('Profile completion fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchCompletionData(); }, [fetchCompletionData]);

  const completedCount = sections?.filter(s => s?.complete)?.length;
  const totalCount = sections?.length || 4;
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isComplete = percentage === 100;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)] mb-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-[var(--color-muted)]" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-5 w-48 bg-[var(--color-muted)] rounded" />
            <div className="h-3 w-32 bg-[var(--color-muted)] rounded" />
            <div className="h-3 w-40 bg-[var(--color-muted)] rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative bg-[var(--color-card)] border rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)] mb-6 overflow-hidden ${
      isComplete ? 'border-[var(--color-success)]/40' : 'border-[var(--color-primary)]/30'
    }`}>
      {/* Background accent */}
      <div className={`absolute top-0 right-0 w-48 h-48 rounded-full opacity-5 -translate-y-1/2 translate-x-1/2 ${
        isComplete ? 'bg-[var(--color-success)]' : 'bg-[var(--color-primary)]'
      }`} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4 relative z-10">
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <CircularProgressRing percentage={percentage} size={76} strokeWidth={6} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isComplete ? (
                <Icon name="CheckCircle" size={28} color="var(--color-success)" />
              ) : (
                <>
                  <span className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)] leading-none">{percentage}%</span>
                  <span className="text-[9px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">done</span>
                </>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              {isComplete ? 'Profile Complete! 🎉' : 'Complete Your Profile'}
            </h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
              {isComplete
                ? 'Your profile is fully set up'
                : `${completedCount} of ${totalCount} sections completed`}
            </p>
            {!isComplete && (
              <div className="mt-1.5 h-1.5 w-40 bg-[var(--color-muted)] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${percentage}%`, backgroundColor: 'var(--color-primary)' }}
                />
              </div>
            )}
          </div>
        </div>
        {isComplete && (
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors flex-shrink-0"
            title="Dismiss"
          >
            <Icon name="X" size={15} color="var(--color-muted-foreground)" />
          </button>
        )}
      </div>

      {/* Section Rows */}
      <div className="flex flex-col gap-2 relative z-10">
        {sections?.map(section => (
          <div
            key={section?.id}
            className={`flex items-center gap-3 p-3 rounded-[var(--radius-sm)] border transition-all ${
              section?.complete
                ? 'bg-[var(--color-success)]/5 border-[var(--color-success)]/20'
                : 'bg-[var(--color-background)] border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              section?.complete ? 'bg-[var(--color-success)]/10' : 'bg-[var(--color-warning)]/10'
            }`}>
              {section?.complete ? (
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
              ) : (
                <Icon name="AlertCircle" size={16} color="var(--color-warning)" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Icon name={section?.icon} size={13} color={section?.complete ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
                <span className={`text-sm font-semibold font-[var(--font-caption)] ${
                  section?.complete ? 'text-[var(--color-success)]' : 'text-[var(--color-foreground)]'
                }`}>{section?.label}</span>
              </div>
              <p className="text-[11px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5 truncate">{section?.description}</p>
            </div>
            {section?.complete ? (
              <span className="text-[10px] font-semibold text-[var(--color-success)] font-[var(--font-caption)] bg-[var(--color-success)]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                ✓ Done
              </span>
            ) : (
              <button
                onClick={() => navigate(section?.link)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity flex-shrink-0 whitespace-nowrap"
              >
                <Icon name="ArrowRight" size={11} color="#fff" />
                {section?.linkLabel}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
